import type { CategorizedAssetItem } from '@/api/modules/assets';

export interface SessionDraftFile {
  file: File;
  preview?: string;
}

export interface SessionDraft {
  text: string;
  files: SessionDraftFile[];
  libraryAssets: CategorizedAssetItem[];
}

interface StoredDraft {
  sessionId: string;
  text: string;
  files: Array<{ blob: Blob; name: string; type: string; lastModified: number }>;
  libraryAssets: CategorizedAssetItem[];
  updatedAt: number;
}

const DB_NAME = 'playgen-client-state';
const STORE_NAME = 'session-drafts';
const DB_VERSION = 1;
const memoryDrafts = new Map<string, StoredDraft>();
const deletedSessionIds = new Set<string>();

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'sessionId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

async function readStored(sessionId: string): Promise<StoredDraft | null> {
  const db = await openDb();
  if (!db) return memoryDrafts.get(sessionId) ?? null;
  try {
    return await new Promise((resolve) => {
      const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(sessionId);
      request.onsuccess = () => resolve((request.result as StoredDraft | undefined) ?? null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return memoryDrafts.get(sessionId) ?? null;
  } finally {
    db.close();
  }
}

export async function loadSessionDraft(sessionId: string): Promise<SessionDraft | null> {
  if (!sessionId || deletedSessionIds.has(sessionId)) return null;
  const stored = await readStored(sessionId);
  if (!stored) return null;
  return {
    text: stored.text ?? '',
    files: (stored.files ?? []).map((item) => ({
      file: new File([item.blob], item.name, { type: item.type, lastModified: item.lastModified }),
      preview: item.type.startsWith('image/') ? URL.createObjectURL(item.blob) : undefined,
    })),
    libraryAssets: stored.libraryAssets ?? [],
  };
}

export async function saveSessionDraft(sessionId: string, draft: SessionDraft): Promise<void> {
  if (!sessionId || deletedSessionIds.has(sessionId)) return;
  const stored: StoredDraft = {
    sessionId,
    text: draft.text,
    files: draft.files.map(({ file }) => ({
      blob: file,
      name: file.name,
      type: file.type,
      lastModified: file.lastModified,
    })),
    libraryAssets: draft.libraryAssets,
    updatedAt: Date.now(),
  };
  memoryDrafts.set(sessionId, stored);
  const db = await openDb();
  if (!db) return;
  try {
    await new Promise<void>((resolve) => {
      const request = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(stored);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  } catch {
    // 内存副本仍可覆盖当前页面生命周期，IndexedDB 失败不阻塞输入。
  } finally {
    db.close();
  }
}

export async function clearSessionDraft(sessionId: string): Promise<void> {
  if (!sessionId) return;
  memoryDrafts.delete(sessionId);
  const db = await openDb();
  if (!db) return;
  try {
    await new Promise<void>((resolve) => {
      const request = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(sessionId);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  } catch {
    // IndexedDB 删除失败不阻塞会话操作；内存副本已经清除。
  } finally {
    db.close();
  }
}

/** 删除会话时留下进程内 tombstone，防止组件切换清理 effect 把旧草稿重新写回。 */
export async function deleteSessionDraft(sessionId: string): Promise<void> {
  deletedSessionIds.add(sessionId);
  await clearSessionDraft(sessionId);
}
